import {
  EActionType,
  UserAssignmentsScope,
  IdFormat,
  PriorityValue,
  ESessionStore,
  EVariableType,
  EPageNames,
  EConfigitProperty,
} from '../../data/Constants';
import {
  createVariableAssignments,
  getConflict,
  addPdmProperties,
  isValidConfigurableSection,
  isSubmodelCountVariable,
  getValueById,
  IsAppOpenedByExtApp,
  IsAppOpenedInIframe,
} from '../../services';
import { SessionStore } from '../../services/SessionStore';
import { appSettings } from '../../settings';
import { IsDefaultFlowInSessionStore } from '../../services/SessionStoreHelperFunctions';
import { TFunction } from 'i18next';

import type {
  ILoadConfiguration,
  ILoadConfigurationPayload,
  IExtendedConfigureResponseOptional,
  IExtendedConfigureResponse,
  IContextDataCountMap,
  IConfiguration,
  IContextData,
  ISection,
  IConflict,
  IAssignment,
  IContextDataModel,
  IVariableAssignment,
  IImagesData,
  IMaterialPropertyAssociations,
  IConfigurationVariable,
  IMaterialImages,
} from '../../../types';
import type {
  IError,
  IConfigurationAction,
  IConfigureResult,
  IImageResponse,
  IPropertyAssociationResponse,
  ILoadConfigurationResult,
} from '../../../types/IApiTypes';

const createOptionalItems = (data: ILoadConfigurationPayload): ILoadConfiguration => {
  const optionalItems = new Map<string, boolean>();
  if (Array.isArray(data.optionalItems)) {
    for (const item of data.optionalItems) {
      optionalItems.set(item, true);
    }
  }
  return { ...data, optionalItems };
};

/**
 * This function extract the data from configure response and returns the load configuration data to the slice
 * @param {IConfigurationaction } action the configuration action
 * @param {IExtendedConfigureResponse} state the configuration state
 * @returns {ILoadConfiguration} returns the load configuration data
 */

export const loadedConfiguration = (
  action: IConfigurationAction,
  state: IExtendedConfigureResponse
): ILoadConfigurationResult => {
  if (action.data) {
    action.data = createOptionalItems(action.data);
  }
  const variableAssignments = createVariableAssignments(action.data.userAssignments);
  const newState: IExtendedConfigureResponse = {
    ...state,
    savedConfiguration: action.data,
    contextData: getContextData(action.data),
    assignments: variableAssignments,
    access: action.access,
  };
  const data = configured(action, newState);
  return {
    savedConfiguration: action.data,
    contextData: data?.contextData,
    access: action.access,
    conflict: data?.conflict,
    data: data?.data,
    assignments: data?.assignments,
    isInvalid: data && (data.isInvalid === false || data.isInvalid) ? data.isInvalid : null,
    acceptedChanges: data?.acceptedChanges,
  };
};

/**
 * This function extract the data from configure response and updated to the state variables
 * @param {IConfigurationaction } action the configuration action
 * @param {IExtendedConfigureResponse} state the configuration state
 * @returns {IExtendedConfigureResponse} updated the the configuration state
 */

export const configured = (
  action: IConfigurationAction,
  state: IExtendedConfigureResponse
): IConfigureResult | IExtendedConfigureResponse => {
  action.configuration = action.configuration ?? action.data.configureResponse ?? undefined;

  if (!action.configuration) {
    return state;
  }

  const assignmentsToRemove = action.configuration.removedAssignments?.variableAssignments;
  let assignments = state.assignments ? [...state.assignments] : [];
  if (action.apiType === EActionType.ClearAll) {
    assignments = assignments.filter((assignedmentVal) =>
      UserAssignmentsScope.find((userVal) => userVal === assignedmentVal.variableId)
    ); //Update UserAssignmentsScope in Constant file if new static id adds up
  }

  const conflict =
    state.isInvalid === false ? getConflict({ ...state, data: { ...action.configuration } }) : null;

  const shortSalesText = state.shortSalesText;

  if (assignmentsToRemove) {
    assignments = removeAssignments(assignmentsToRemove, shortSalesText, assignments);
  }
  const args = action.configuration.arguments?.Configuration || {};
  const submodels = Object.keys(args).filter((e) => {
    return e.endsWith(IdFormat.Suffix.SubmodelId) && args[e] >= 1;
  });
  submodels.forEach((m) => {
    const ass = assignments.find((a) => a.variableId === m);
    if (!ass) {
      assignments.push({
        type: 'Singleton',
        variableId: m,
        value: args[m].toString(),
        exclude: false,
        priority: PriorityValue.High,
      });
    } else if (Number(ass.value) >= 1) {
      ass['priority'] = PriorityValue.High;
    }
  });

  sanitizeConfiguration(action.configuration, state.contextData);

  const countVariables: IContextDataCountMap = {};
  if (state.contextData?.countVariables) {
    Object.keys(state.contextData?.countVariables).forEach((k) => {
      countVariables[k] = null;
    });
  }

  const bundledFeatures: { [key: string]: boolean } = {};
  mapCountAndBundleFeatures(action.configuration.sections, countVariables, bundledFeatures);

  //Update the variable and value  configit properties using  content folder propert Associations response
  action.configuration.sections = addPdmProperties(
    action.configuration.sections,
    state.propertyAssociations,
    state.propertyDefinition
  );
  //remove once real properties are available
  // fixSubModelViews( action.configuration );
  const assignmentsBefore = state.assignments ? [...state.assignments] : [];
  return checkInvalid(
    {
      ...state,
      contextData: { ...state.contextData, countVariables: { ...countVariables } },
      bundledFeatures: bundledFeatures,
    },
    action,
    conflict,
    assignments,
    assignmentsToRemove,
    assignmentsBefore
  );
};

const sanitizeConfiguration = (configuration: IConfiguration, contextData: IContextData | null) => {
  configuration.sections = configuration.sections?.filter((s) => sanitizeSection(contextData, s));
};

const sanitizeSection = (contextData: IContextData | null, section: ISection) => {
  section.sections = section.sections?.filter((s) => sanitizeSection(contextData, s));
  return isValidConfigurableSection(contextData, section);
};

const checkInvalid = (
  state: IExtendedConfigureResponseOptional,
  action: IConfigurationAction,
  conflict: IConflict | null,
  assignments: IAssignment[],
  assignmentsToRemove: IVariableAssignment[] | undefined,
  assignmentsBefore: IAssignment[]
): IConfigureResult => {
  if (state.isInvalid === null) {
    if (assignmentsToRemove?.length) {
      return {
        conflict,
        data: action.configuration,
        assignments,
        isInvalid: true,
        acceptedChanges: false,
        contextData: state.contextData,
        assignmentsBefore: assignmentsBefore,
      };
    } else {
      return {
        conflict,
        data: action.configuration,
        assignments,
        isInvalid: false,
        acceptedChanges: false,
        contextData: state.contextData,
        assignmentsBefore: assignmentsBefore,
      };
    }
  } else {
    if (state.isInvalid) {
      //If user accepts proposed changes
      return {
        conflict: null,
        data: action.configuration,
        assignments,
        isInvalid: false,
        acceptedChanges: true,
        contextData: state.contextData,
        assignmentsBefore: assignmentsBefore,
      };
    }
    return {
      conflict,
      data: action.configuration,
      assignments,
      isInvalid: state.isInvalid,
      contextData: state.contextData,
      assignmentsBefore: assignmentsBefore,
    };
  }
};

/**
 * Generates and returns the context data from model context
 * @param {ILoadConfiguration} data The data received from read api call
 * @returns {IContextData} context data which maps model id to model context
 */
function getContextData(data?: ILoadConfiguration): IContextData {
  if (!data) {
    return { models: undefined, countVariables: undefined };
  }
  const modelContext = data.modelContext;
  const { rootModel, subModels } = modelContext;
  const models: { [key: string]: IContextDataModel } = {
    [rootModel.id]: { ...rootModel, isRoot: true },
  };
  if (rootModel.id) {
    SessionStore.set(ESessionStore.ProductId, rootModel.id);
  }
  const countVariables: IContextDataCountMap = {};
  subModels.forEach((sm) => {
    models[sm.id] = sm;
    if (sm.subModelVariableIds?.length) {
      sm.subModelVariableIds?.forEach((v) => {
        countVariables[v] = null;
      });
    }
  });

  return { models, countVariables };
}

/**
 * Returns an object that maps the count variables from configure response to the count variables ids in context data
 * @param {ISection[]} sections the sections received in configure response
 * @param {IContextDataCountMap} countVariables the object to store the map
 * @param {bundledFeatures} bundledFeatures the object to store the map of bundle features
 * @returns {void}
 */

function mapCountAndBundleFeatures(
  sections: ISection[],
  countVariables: IContextDataCountMap,
  bundledFeatures: { [key: string]: boolean }
) {
  const setVariable = (varible: IConfigurationVariable) => {
    varible.values
      .filter((val) => val.assigned)
      ?.forEach((val) => {
        const vl = val.properties?.find((vp) => vp.id === EConfigitProperty.BundleContent);
        vl?.value
          ?.toString()
          .split(',')
          .forEach((vid) => (bundledFeatures[vid] = true));
      });
    if (varible.id in countVariables || isSubmodelCountVariable(varible)) {
      countVariables[varible.id] = varible;
    }
  };
  sections.forEach((section: ISection) => {
    section.variables.forEach((varible) => {
      setVariable(varible);
    });

    if (section.sections?.length) {
      mapCountAndBundleFeatures(section.sections, countVariables, bundledFeatures);
    }
  });
}

/**
 * This function remove the conflicting assignments from the existing assignemnts
 * @param {IVariableAssignment[]} assignmentsToRemove remove assignemnts
 * @param {Map<string, Map<string, string>>} shortSalesText short sales text data
 * @param { IAssignment[] } assignments current assignemnts
 * @returns {IAssignment[]} return the assignments
 */

const removeAssignments = (
  assignmentsToRemove: IVariableAssignment[],
  shortSalesText: Map<string, Map<string, string>>,
  assignments: IAssignment[]
): IAssignment[] => {
  const validAssignments = [...assignments];
  for (const assignmentToRemove of assignmentsToRemove) {
    if (appSettings.UseShortSalesText && shortSalesText) {
      //Assign short sales text for variable
      const variableText = getValueById(shortSalesText, assignmentToRemove.variable.id);
      assignmentToRemove.variable.shortSalesText = variableText?.toString();

      //Assign short sales text for variable value
      const valueText = getValueById(
        shortSalesText,
        assignmentToRemove.value.value?.toString() ?? ''
      );
      assignmentToRemove.value.shortSalesText = valueText?.toString();
    }

    let index = -1;
    //Compare the feature value name for Numeric features and rest all type features compare the value
    if (assignmentToRemove.variable.valueType === EVariableType.Number) {
      index = validAssignments.findIndex(
        (a) =>
          a.variableId === assignmentToRemove.variable.id &&
          a.value === assignmentToRemove.value.name
      );
    } else {
      index = validAssignments.findIndex(
        (a) =>
          a.variableId === assignmentToRemove.variable.id &&
          a.value === assignmentToRemove.value.value
      );
    }
    if (index >= 0) {
      validAssignments.splice(index, 1);
    }
  }
  return validAssignments;
};

/**
 * This function set the current  price  to the state
 * @param { IImageResponse } action api payload
 * @param { IExtendedConfigureResponse } state configuration state
 * @returns { void }
 */

export function setProductImages(action: IImageResponse, state: IExtendedConfigureResponse): void {
  action.productImages =
    action.productImages && typeof action.productImages === 'string'
      ? JSON.parse(action.productImages)
      : action.productImages;

  const updateProductImages = new Map<string, IImagesData>(
    state.productImages?.get(action.modelId)
  );
  if (action.modelId && action.productImages) {
    action.productImages?.forEach((data: IImagesData) => {
      if (data.materialCode && !(data.materialCode in updateProductImages)) {
        updateProductImages.set(data.materialCode, data);
      }
    });

    state.productImages?.set(action.modelId, updateProductImages);
  }
}

/**
 * This function set the current  property associations   to the state
 * @param { IPropertyAssociationResponse } action api payload
 * @param { IExtendedConfigureResponseOptional } state configuration state
 * @returns { void }
 */

export function setPropertyAssociations(
  action: IPropertyAssociationResponse,
  state: IExtendedConfigureResponseOptional
): void {
  if (action.modelId) {
    const propertyAssociations = new Map<string, IMaterialPropertyAssociations>(
      state.propertyAssociations?.get(action.modelId)
    );
    if (action.propertiesAssociation) {
      action.propertiesAssociation?.forEach((data: IMaterialPropertyAssociations) => {
        if (data.materialCode && !(data.materialCode in propertyAssociations)) {
          propertyAssociations.set(data.materialCode, data);
        }
      });
    }
    state.propertyAssociations?.set(action.modelId, propertyAssociations);
  }
}

export const getErrorData = (t: TFunction): IError => {
  const isAppAccessedByEstore =
    (IsAppOpenedByExtApp() || IsAppOpenedInIframe()) && !IsDefaultFlowInSessionStore();
  const errorMessage = isAppAccessedByEstore
    ? 'errorMessages.EStore.ErrorMessage'
    : 'errorMessages.CPQ.ErrorMessage';
  return { response: null, code: 404, message: t(errorMessage), page: EPageNames.Configurator };
};

/**
 * Updates Sales text, ordering instructions and images for submodels in the configuration state
 * @param {IExtendedConfigureResponse} state - The configuration state
 * @param {string} language - The language code for sales text
 * @param {boolean} updateAllData - Flag to indicate whether to update all data i.e. ORI and images
 * @returns {void}
 */
export const updateSubmodelDataInState = (
  state: IExtendedConfigureResponse,
  language: string,
  updateAllData: boolean = false
) => {
  if (state.savedConfiguration) {
    const { rootModel } = state.savedConfiguration.modelContext;
    const submodelData = state.subModelData.get(rootModel.id);
    submodelData?.forEach((data) => {
      data.texts.forEach((text) => {
        if (text.id === language) {
          state.longSalesText?.set(data.id, new Map([[data.id, text.long]]));
          state.shortSalesText?.set(data.id, new Map([[data.id, text.short]]));
        }
      });
      if (updateAllData) {
        state.orderingInstructions?.set(data.id, data.ori);
        const imagesData: IMaterialImages[] = [];
        data?.images?.forEach((image: string) => {
          const imageData: IMaterialImages = {
            url: image,
            isDefault: true,
          };
          imagesData.push(imageData);
        });
        const imageData = {
          modelId: data.id,
          productImages: [
            {
              materialCode: data.id,
              materialImages: imagesData,
            },
          ],
        };
        setProductImages(imageData, state);
      }
    });
  }
};
